/**
 * The overview cards' shared parts: the data (the selected bot with its success score,
 * its activity), the activity range and chart, the success ring and where a score step
 * leads. Used by the classic overview (Overview.js) and the AI Studio start page
 * (Mo's welcome, components/agentStudio/WelcomeMessage.js), so both always show the same.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { loadRichBots, useRichBots } from '../components/agentStudio/studioData';
import './Overview.css';

// ── Activity helpers (mirrors the bot-card chart in bots.js) ────────────────
const getUserDateFormat = () => {
  const stored = localStorage.getItem('date_format');
  if (stored === 'DD-MM-YYYY' || stored === 'MM-DD-YYYY' || stored === 'YYYY-MM-DD') {
    return stored;
  }
  return 'YYYY-MM-DD';
};

const formatActivityKey = (key, pattern) => {
  const parts = key.split('-');
  if (parts.length !== 3) return key;
  const [mm, dd, yyyy] = parts;
  if (pattern === 'DD-MM-YYYY') return `${dd}-${mm}-${yyyy}`;
  if (pattern === 'MM-DD-YYYY') return `${mm}-${dd}-${yyyy}`;
  return `${yyyy}-${mm}-${dd}`;
};

/** An ISO day (YYYY-MM-DD) in the user's date format setting, as the activity charts show it. */
export const formatDayForUser = (iso) => {
  const [y, m, d] = String(iso).split('-');
  return d ? formatActivityKey(`${m}-${d}-${y}`, getUserDateFormat()) : String(iso);
};

// Selectable ranges for the Activity chart. The largest (RANGE_MAX) is what
// we request from the server; smaller ranges are sliced client-side so the
// switcher is instant and needs no re-fetch.
const RANGE_OPTIONS = [7, 14, 31, 90];
const RANGE_MAX = RANGE_OPTIONS[RANGE_OPTIONS.length - 1];
const DEFAULT_RANGE = 14;

const getLastNKeys = (n) => {
  const keys = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  for (let i = n - 1; i >= 0; i--) {
    const day = new Date(d);
    day.setDate(d.getDate() - i);
    const mm = String(day.getMonth() + 1).padStart(2, '0');
    const dd = String(day.getDate()).padStart(2, '0');
    const yyyy = String(day.getFullYear());
    keys.push(`${mm}-${dd}-${yyyy}`);
  }
  return keys;
};

// The dedicated /bots/activity endpoint returns a clean { "MM-DD-YYYY": n }
// object, but tolerate a JSON string too (defensive).
const normalizeActivityMap = (m) => {
  if (!m) return {};
  if (typeof m === 'string') {
    try {
      m = JSON.parse(m);
    } catch {
      return {};
    }
  }
  return m && typeof m === 'object' ? m : {};
};

// Build the full RANGE_MAX-day series once. `x` is the day offset relative to
// today (today = 0, oldest = -(RANGE_MAX - 1)), so the chart can show fewer
// days by narrowing the x-axis domain rather than dropping data points — that
// lets the line extend/shrink smoothly with today anchored to the right edge.
const getFullActivitySeries = (rawMap) => {
  const map = normalizeActivityMap(rawMap);
  const format = getUserDateFormat();
  const keys = getLastNKeys(RANGE_MAX); // oldest -> newest
  const n = keys.length;
  return keys.map((k, i) => ({
    x: i - (n - 1),
    date: k,
    displayDate: formatActivityKey(k, format),
    value: Number(map[k] ?? 0),
  }));
};

const ActivityTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    const point = payload[0].payload;
    return (
      <div className="ov-chart-tooltip">
        <div className="ov-chart-tooltip-date">{point.displayDate}</div>
        <div className="ov-chart-tooltip-value">
          {payload[0].value} {t('ov_responses', { count: payload[0].value })}
        </div>
      </div>
    );
  }
  return null;
};

const scoreColor = (pct) => {
  if (pct >= 100) return '#5616ea';
  if (pct >= 70) return '#16a34a';
  if (pct >= 40) return '#f59e0b';
  return '#ef4444';
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Tween a number from 0 → target on mount (and re-tween whenever target
// changes), easing out. Used to drive the success ring's fill + its percentage
// label so they animate in together. Honours prefers-reduced-motion.
// startAtTarget: show the value at once (data the page already had); later changes still count up
export function useCountUp(target, duration = 800, startAtTarget = false) {
  const reduce = prefersReducedMotion();
  const [value, setValue] = useState(reduce || startAtTarget ? target : 0);
  const fromRef = useRef(reduce || startAtTarget ? target : 0);
  useEffect(() => {
    if (reduce) {
      fromRef.current = target;
      setValue(target);
      return undefined;
    }
    const from = fromRef.current;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let raf;
    let startTs = null;
    const step = (ts) => {
      if (startTs === null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / duration);
      const v = from + (target - from) * easeOutCubic(p);
      fromRef.current = v;
      setValue(v);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduce]);
  return value;
}

export function Ring({ percentage }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  // The fill sweeps up from empty and the label counts up in lockstep — both
  // read the same tweened value so they never drift apart.
  const shown = useCountUp(percentage, 850);
  const offset = c * (1 - Math.max(0, Math.min(100, shown)) / 100);
  const color = scoreColor(percentage);
  return (
    <div className="ov-ring">
      <svg viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} className="ov-ring-track" />
        <circle
          cx="55"
          cy="55"
          r={r}
          className="ov-ring-value"
          stroke={color}
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 55 55)"
        />
      </svg>
      <div className="ov-ring-center">
        <span className="ov-ring-pct" style={{ color }}>
          {Math.round(shown)}
          <i>%</i>
        </span>
      </div>
    </div>
  );
}

// Where a success-score step leads (SuccessScoreModal onSettingsClick).
export const SCORE_TARGETS = {
  knowledge: '/knowledge/website-links',
  behavior: '/agent-studio/behavior',
  design: '/deploy/design',
  live_chat: '/live/settings',
  notifications: '/notifications',
  skills: '/agent-studio/skills',
  leads: '/agent-studio/skills',
};

// The data behind the cards: the selected bot from the rich list (it carries the
// success score) and its activity series. Both refresh when a test chat reports
// new usage (ub:usage-updated).
export function useOverviewData(selectedBotId) {
  // Success score isn't carried by the global context, so fetch the rich list
  // here and pick the active bot. Activity is loaded separately (below) from a
  // dedicated, lightweight endpoint so it never weighs down this query.
  // shared with the AI Studio (page-session cache): shown at once when it is there, refreshed in the background
  const richBots = useRichBots();
  const loadRich = loadRichBots;

  // Activity for the chart comes from the dedicated /bots/activity endpoint and
  // only for the selected bot — fetched on demand (widest window, sliced
  // client-side by the range switcher). null while loading.
  const [activityMap, setActivityMap] = useState(null);
  const loadActivity = useCallback(async (botId, { silent = false } = {}) => {
    if (!botId) return;
    // `silent` keeps the current chart on screen while refetching (used by the
    // live usage-update refresh) instead of blanking it to a loading spinner.
    if (!silent) setActivityMap(null);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots/activity?bot_id=${encodeURIComponent(
        botId
      )}&days=${RANGE_MAX}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error();
      const data = await res.json(); // { [botId]: { 'MM-DD-YYYY': count } }
      setActivityMap((data && data[botId]) || {});
    } catch {
      setActivityMap({});
    }
  }, []);
  useEffect(() => {
    loadActivity(selectedBotId);
  }, [loadActivity, selectedBotId]);

  // When the test-chat (ChatModal) closes after sending messages it fires
  // `ub:usage-updated`. Re-pull the activity chart + success list so the
  // "responses" total and graph reflect the just-sent messages live. The
  // headline "AI responses" tile is driven by the global bot context, which
  // refreshes itself off the same event.
  useEffect(() => {
    const onUsageUpdated = () => {
      loadActivity(selectedBotId, { silent: true });
      loadRich();
    };
    window.addEventListener('ub:usage-updated', onUsageUpdated);
    return () => window.removeEventListener('ub:usage-updated', onUsageUpdated);
  }, [loadActivity, loadRich, selectedBotId]);

  const richBot = useMemo(
    () => (richBots || []).find((b) => String(b.id) === String(selectedBotId)) || null,
    [richBots, selectedBotId]
  );
  return { richBot, activityMap, loadRich };
}

// The Activity range (days, persisted) and the animated left edge of the x-axis.
export function useActivityRange() {
  // Activity chart range (days). Persisted so the choice sticks across visits.
  const [rangeDays, setRangeDays] = useState(() => {
    const stored = Number(localStorage.getItem('ov_activity_range'));
    return RANGE_OPTIONS.includes(stored) ? stored : DEFAULT_RANGE;
  });
  const changeRange = useCallback((days) => {
    setRangeDays(days);
    localStorage.setItem('ov_activity_range', String(days));
  }, []);
  // Left edge of the chart's x-axis window (today is 0). Tweened toward the
  // selected range so the line slides open/closed instead of cutting.
  const rangeToDomainStart = (days) => -(days - 1);
  const [domainStart, setDomainStart] = useState(() =>
    rangeToDomainStart(
      RANGE_OPTIONS.includes(Number(localStorage.getItem('ov_activity_range')))
        ? Number(localStorage.getItem('ov_activity_range'))
        : DEFAULT_RANGE
    )
  );
  const domainStartRef = useRef(domainStart);

  // Animate the x-axis left edge toward the selected range. recharts doesn't
  // tween domains, so we drive it frame by frame; the right edge (today = 0)
  // stays pinned, so the line grows/shrinks from the left.
  useEffect(() => {
    const from = domainStartRef.current;
    const to = rangeToDomainStart(rangeDays);
    if (from === to) return undefined;
    // Respect reduced-motion: snap to the new window without tweening.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      domainStartRef.current = to;
      setDomainStart(to);
      return undefined;
    }
    const duration = 380;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let raf;
    let startTs = null;
    const step = (ts) => {
      if (startTs === null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / duration);
      const val = from + (to - from) * easeOutCubic(p);
      domainStartRef.current = val;
      setDomainStart(val);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [rangeDays]);

  return { rangeDays, changeRange, domainStart };
}

// The full series, and for the selected window the headline total and the y-scale.
export function useActivityStats(activityMap, rangeDays) {
  // Full max-window series stays mounted; the visible window is the x-axis
  // domain (animated, useActivityRange), so changing the range extends/shrinks
  // the line instead of swapping one chart for another.
  const series = useMemo(
    () => (activityMap ? getFullActivitySeries(activityMap) : []),
    [activityMap]
  );
  // Only the selected window feeds the headline total and the y-scale.
  const visible = useMemo(
    () => series.filter((d) => d.x >= -(rangeDays - 1)),
    [series, rangeDays]
  );
  const total = useMemo(() => visible.reduce((sum, d) => sum + d.value, 0), [visible]);
  const yMax = useMemo(() => visible.reduce((m, d) => Math.max(m, d.value), 0), [visible]);
  return { series, total, yMax };
}

// The 7d / 14d / 31d / 90d switch with its sliding pill.
export function RangeSwitch({ rangeDays, onChange, className = '' }) {
  const { t } = useTranslation();
  return (
    <div className={`ov-range ${className}`} role="group" aria-label={t('ov_activity_range_aria')}>
      {/* Single white pill that slides to the selected option. Width
          subtracts the 6px track padding (2 x 3px) so it lines up with
          each equal-width button; transform moves it one slot per index. */}
      <span
        className="ov-range-thumb"
        style={{
          width: `calc((100% - 6px) / ${RANGE_OPTIONS.length})`,
          transform: `translateX(${Math.max(0, RANGE_OPTIONS.indexOf(rangeDays)) * 100}%)`,
        }}
        aria-hidden="true"
      />
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`ov-range-btn${rangeDays === opt ? ' active' : ''}`}
          onClick={() => onChange(opt)}
          aria-pressed={rangeDays === opt}
        >
          {t('ov_range_days_short', { n: opt })}
        </button>
      ))}
    </div>
  );
}

// The activity area chart (a spinner while the data loads).
export function ActivityChart({ activityMap, series, domainStart, yMax, gradientId = 'ovActivityFill',
  margin = { top: 10, right: 8, bottom: 14, left: 0 } }) {
  if (!activityMap) {
    return (
      <div className="ov-chart-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={series} margin={margin}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5616ea" stopOpacity={0.26} />
            <stop offset="100%" stopColor="#5616ea" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="x" type="number" hide domain={[domainStart, 0]} allowDataOverflow />
        <YAxis hide allowDecimals={false} domain={[0, yMax + 1]} />
        <Tooltip content={<ActivityTooltip />} cursor={{ stroke: '#d9cffa', strokeWidth: 1 }} />
        <Area
          type="monotone"
          dataKey="value"
          stroke="#5616ea"
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{ r: 4, fill: '#5616ea', stroke: '#fff', strokeWidth: 2 }}
          // Draw the line/area in on first paint. Only the data drives a
          // re-animation, so switching the range (which moves the x-axis
          // domain, not the data) won't re-trigger it.
          isAnimationActive={!prefersReducedMotion()}
          animationDuration={1000}
          animationEasing="ease-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
